import { useGetUserProfileByAddress } from '@/api/query'
import { AvatarCircle } from '@/components/atoms/Avatar'
import { GradientText } from '@/components/atoms/GradientText/GradientText'
import { StepBadge, StepBadgeColor } from '@/components/atoms/StepBadge/StepBadge'
import { NoResultsFallback } from '@/components/molecules/NoResultsFallback/NoResultsFallback'
import { useAppModal, AppModals } from '@/context/ModalProvider'
import { Eye } from '@/icons'
import { Box, SimpleGrid, Skeleton, Flex, Text } from '@chakra-ui/react'
import { AnimatePresence, motion } from 'framer-motion'
import { BigNumberInBase } from '@injectivelabs/utils'
import { memo } from 'react'
import { useTranslation } from 'next-i18next/pages'
import { Coin } from '@ninjablaze/sdk-ts'
import { getTokenByDenom } from '@/services/token-service'
import { SupportedCurrencies, TokenMoney } from '@ninjablaze/sdk-ts'
import { FiatOrCryptoAmount } from '@/components/atoms/Amount/Amount'
import { useFiatCurrencyStore } from '@/context/FiatCurrencyStore'
import { USDC_NEW_DENOM } from '@/constants/constants'
import Link from 'next/link'

interface GameRecord {
  id: string
  player: string
  payout: Coin
  bet: Coin
  betUsd?: string
  payoutUsd?: string
}

export const LastGamesList = memo(
  ({
    error,
    loading,
    games,
    gameDetailsCallback,
    viewAllHref,
  }: {
    error: boolean
    loading: boolean
    games: Array<GameRecord>
    gameDetailsCallback: (id: string) => void
    viewAllHref?: string
  }) => {
    const { t } = useTranslation()

    if (error) {
      return <NoResultsFallback styles={{ mt: 4 }} text={t('common.error-fetching-games')} />
    }

    if (loading) {
      return (
        <Box mt={4} pos={'relative'} overflow={'hidden'} h={'full'}>
          <SimpleGrid w={'full'} pos={{ base: 'static', xl: 'absolute' }} gap={3} rowGap={3}>
            {Array.from({ length: 11 }, (_, k) => (
              <Skeleton key={k} startColor={'ninja.700'} rounded={'xl'} endColor={'ninja.800'} h={'52px'} w={'full'} />
            ))}
          </SimpleGrid>
        </Box>
      )
    }

    return (
      <Flex flexDir={'column'} h={'full'} gap={2}>
        <Box pos={'relative'} overflow={'hidden'} flex={1}>
          <Flex w={'full'} flexDir={'column-reverse'} pos={{ base: 'static', xl: 'absolute' }}>
            <AnimatePresence>
              {games.map((game) => (
                <motion.div
                  key={game.id}
                  initial={{ opacity: 0, y: -50 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                >
                  <UserProfile
                    key={game.id}
                    bet={game.bet}
                    status={new BigNumberInBase(game.payout.amount).isGreaterThan(game.bet.amount) ? 'WIN' : 'LOSE'}
                    payout={game.payout}
                    address={game.player}
                    betUsd={game.betUsd}
                    payoutUsd={game.payoutUsd}
                    gameDetailsCallback={() => gameDetailsCallback(game.id)}
                  />
                </motion.div>
              ))}
            </AnimatePresence>
          </Flex>
          <Box
            display={{ base: 'none', xl: 'block' }}
            pos={'absolute'}
            bgGradient={`linear-gradient(0deg, #1D1C49 0%, transparent 100%)`}
            h={'100px'}
            right={0}
            left={0}
            bottom={0}
          />
        </Box>
        {viewAllHref && games.length > 0 && (
          <Flex
            as={Link}
            href={viewAllHref}
            py={3}
            px={4}
            justifyContent={'center'}
            alignItems={'center'}
            bg={'ninja.500'}
            rounded={'xl'}
            color={'white'}
            fontSize={'sm'}
            fontWeight={700}
            cursor={'pointer'}
            flexShrink={0}
            _hover={{ bg: '#3D3C70' }}
            transition={'0.15s'}
          >
            {t('common.view-all-my-games')}
          </Flex>
        )}
      </Flex>
    )
  }
)

const UserProfile = ({
  address,
  bet,
  status,
  payout,
  betUsd,
  payoutUsd,
  gameDetailsCallback,
}: {
  address: string
  bet: Coin
  status: string
  payout: Coin
  betUsd?: string
  payoutUsd?: string
  gameDetailsCallback: () => void
}) => {
  const { data } = useGetUserProfileByAddress(address)
  const modal = useAppModal()
  const isUsd = useFiatCurrencyStore((s) => s.isUsd())

  const isUsdcToken = getTokenByDenom(bet.denom)?.currency === SupportedCurrencies.USDC
  const useFiatEquivalent = !isUsd && !isUsdcToken

  const displayBet: Coin = useFiatEquivalent && betUsd ? { amount: betUsd, denom: USDC_NEW_DENOM } : bet
  const displayPayout: Coin = useFiatEquivalent && payoutUsd ? { amount: payoutUsd, denom: USDC_NEW_DENOM } : payout

  const numberOfNfts = data?.nfts?.totalCount
  const isNftOwner = Boolean(numberOfNfts)

  return (
    <Flex
      py={3}
      alignItems={'center'}
      justifyContent={'space-between'}
      borderBottom={'1px solid'}
      borderColor={'ninja.500'}
      minH={16}
      gap={2}
    >
      <Flex flexBasis={'full'} gap={2} alignItems={'center'} justifyContent={'space-between'}>
        <Flex
          alignItems={'center'}
          gap={2}
          cursor={'pointer'}
          onClick={() => modal.openModalNew(AppModals.UserStats, { address })}
        >
          <AvatarCircle size={'sm'} src={data?.user?.profileAvatar} />
          <Box>
            {isNftOwner ? (
              <GradientText
                numberOfNfts={numberOfNfts!}
                props={{
                  fontWeight: 600,
                  fontSize: 'sm',
                }}
              >
                {data?.user?.profileUserName}
              </GradientText>
            ) : (
              <Text color={'ninja.100'} fontSize={'sm'} fontWeight={'500'}>
                {data?.user?.profileUserName}
              </Text>
            )}
            <Flex gap={1} alignItems={'center'}>
              <FiatOrCryptoAmount
                value={new TokenMoney(displayBet.amount, displayBet.denom)}
                wrapperStyles={{ fontSize: 'xs' }}
                amountStyles={{ color: 'ninja.200' }}
                currencyStyles={{ color: 'ninja.300' }}
                iconStyles={{ display: 'none' }}
              />
              <Text color={status === 'LOSE' ? '#FF3288' : '#4EFF60'} fontWeight={700} fontSize={'xs'}>
                {'x' + new BigNumberInBase(payout.amount).dividedBy(bet.amount).toFixed(2)}
              </Text>
            </Flex>
          </Box>
        </Flex>
        {status === 'WIN' && (
          <StepBadge
            content={
              <FiatOrCryptoAmount
                value={new TokenMoney(displayPayout.amount, displayPayout.denom)}
                wrapperStyles={{ fontSize: 'xs' }}
                currencyStyles={{ color: 'ninja.300' }}
                iconStyles={{ display: 'none' }}
              />
            }
            color={StepBadgeColor.GREEN}
          />
        )}
      </Flex>
      <Box
        flexShrink={0}
        onClick={gameDetailsCallback}
        cursor={'pointer'}
        pr={0}
        _hover={{
          opacity: 0.75,
        }}
      >
        <Eye />
      </Box>
    </Flex>
  )
}
